import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { CreateVirtualCardScreen } from 'src/app/CreateVirtualCardForm/CreateVirtualCardScreen'
import {
  createSetReceiptAttachmentIds,
  getOrgMembers,
} from 'src/app/RequestVirtualCard/redux/requestVirtualCard.actions'
import {
  selectErrorDetailMapFromState,
  selectFilteredRecentUserEmailOptions,
  selectFilteredUserEmailOptions,
  selectPostingVirtualCardFromState,
  selectReceiptAttachmentModelsList,
  selectTopLevelErrorFromState,
  selectUploadingAttachmentsFromState,
  selectUsersFullPaginatedData,
} from 'src/app/RequestVirtualCard/redux/requestVirtualCard.selectors'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import RequestVirtualCardIssuerCycleContent from 'src/components/RequestVirtualCardIssuerCycleContent/RequestVirtualCardIssuerCycleContent'
import RequestVirtualCardRecurringContent from 'src/components/RequestVirtualCardRecurringContent/RequestVirtualCardRecurringContent'
import RequestVirtualCardStandardContent from 'src/components/RequestVirtualCardStandardContent/RequestVirtualCardStandardContent'
import RightFlyout, {
  RightFlyoutBodyWidths,
} from 'src/components/RightFlyout/RightFlyout'
import { UserDropdownInfiniteScrollProps } from 'src/components/UserDropdown/UserDropdown'
import { SupportedBrands } from 'src/enums'
import VirtualCardTypePicker from 'src/feature/VirtualCardTypePicker/VirtualCardTypePicker'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useFlyoutDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { uploadAttachments } from 'src/redux/actions/data/receiptAttachments.actions'
import { postVirtualCardAndOpenToastAndCloseFlyout } from 'src/redux/actions/data/virtualCards.actions'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectCreditCardsActiveSourceCountStatisticsFromState } from 'src/redux/selectors/data/statistics.selectors'
import {
  selectAppName,
  selectBrandName,
} from 'src/redux/selectors/session.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { PostVirtualCardRequest } from 'src/requests/postVirtualCard.request'

export enum RequestVirtualCardScreen {
  Pick,
  Standard,
  Recurring,
  DecliningBalance,
  IssuerCycle,
}

const RequestVirtualCard: React.FC = () => {
  const dispatch = useFlyoutDispatch()

  const receiptAttachments = useSelector(selectReceiptAttachmentModelsList)
  const uploadingAttachmentThumbnails = useSelector(
    selectUploadingAttachmentsFromState,
  )
  const postingVirtualCard = useSelector(selectPostingVirtualCardFromState)
  const topLevelError = useSelector(selectTopLevelErrorFromState)
  const errorDetailMap = useSelector(selectErrorDetailMapFromState)
  const usersFullPaginatedData = useSelector(selectUsersFullPaginatedData)
  const filteredUserEmailOptions = useSelector(selectFilteredUserEmailOptions)
  const filteredRecentUserEmailOptions = useSelector(
    selectFilteredRecentUserEmailOptions,
  )
  const userOrganization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)
  const activeRCNs = useSelector(
    selectCreditCardsActiveSourceCountStatisticsFromState,
  )
  const appName = useSelector(selectAppName)
  const brandName = useSelector(selectBrandName)
  const hideRecurringOption = brandName === SupportedBrands.Jpm
  const translate = useTranslate()

  useEffect((): void => {
    if (!statisticsLoaded.creditCardsActiveSourceCountStatisticsLoaded) {
      dispatchStatistics([
        appStatisticsKeys.CreditCardsActiveSourceCountStatistics,
      ])
    }
  }, [statisticsLoaded, dispatchStatistics])

  const dispatchUploadAttachments = (
    attachments: PostReceiptAttachmentRequest[],
  ): void => {
    dispatch(uploadAttachments(attachments))
  }
  const dispatchPostVirtualCardAndOpenToastAndCloseFlyout = (
    body: Partial<PostVirtualCardRequest>,
  ): void => {
    dispatch(postVirtualCardAndOpenToastAndCloseFlyout(body))
  }

  const deleteAttachment = (attachmentId: string): void => {
    dispatch(
      createSetReceiptAttachmentIds(
        receiptAttachments
          .filter((att) => att.id !== attachmentId)
          .map((att) => att.id),
      ),
    )
  }

  const [screen, setScreen] = useState(RequestVirtualCardScreen.Pick)

  useEffect((): void => {
    dispatch(createSetReceiptAttachmentIds([]))
  }, [screen])

  const fetchUsers = (filterInput?: string, page?: number): void => {
    if (userOrganization === undefined) return
    dispatch(
      getOrgMembers(userOrganization.id, {
        search: filterInput,
        page: page,
        perPage: 10,
        showDeactivatedUsers: false,
        renderMetrics: false,
      }),
    )
  }

  const userInfiniteScrollProps: UserDropdownInfiniteScrollProps = {
    fetch: fetchUsers,
    ...usersFullPaginatedData,
  }

  return (
    <RightFlyout width={RightFlyoutBodyWidths.Action}>
      <Helmet>
        <title>{`Request a Virtual Card | ${appName}`}</title>
        <meta
          name="description"
          content={`Request a Virtual Card page of ${appName}`}
        />
      </Helmet>
      {screen === RequestVirtualCardScreen.Pick &&
        (!statisticsLoaded.creditCardsActiveSourceCountStatisticsLoaded ? (
          <LoadingBackgroundPage />
        ) : (
          <VirtualCardTypePicker
            setScreen={(createScreen) => {
              const screenMap: Partial<
                Record<CreateVirtualCardScreen, RequestVirtualCardScreen>
              > = {
                [CreateVirtualCardScreen.Standard]:
                  RequestVirtualCardScreen.Standard,
                [CreateVirtualCardScreen.Recurring]:
                  RequestVirtualCardScreen.Recurring,
                [CreateVirtualCardScreen.DecliningBalance]:
                  RequestVirtualCardScreen.DecliningBalance,
                [CreateVirtualCardScreen.IssuerCycle]:
                  RequestVirtualCardScreen.IssuerCycle,
              }
              const mappedScreen = screenMap[createScreen]
              if (mappedScreen !== undefined) {
                setScreen(mappedScreen)
              }
            }}
            title={<>{translate('Request a Virtual Card')}</>}
            pickerSubtitle={
              <>
                {translate(
                  "Choose the type of card you need to use. Once it's approved, you can use it right away!",
                )}
              </>
            }
            userHasOrg={!!userOrganization}
            hasNoRCNs={activeRCNs === 0}
            noRCNsTitle={translate(
              "Your organization hasn't registered a company card yet",
            )}
            noRCNsBody={translate(
              "Virtual cards are created from registered company credit cards. Once a card is registered, you'll be able to request virtual cards.",
            )}
            standardDisabled={!userOrganization && !!activeRCNs}
            recurringDisabled={!userOrganization && !!activeRCNs}
            decliningBalanceDisabled={!userOrganization && !!activeRCNs}
            issuerCycleDescription={translate(
              'Request a recurring card with a recurring credit limit. Available credit resets automatically at the end of each billing cycle.',
            )}
            decliningBalanceDescription={translate(
              'Request a virtual card with a fixed spending limit. The available balance decreases as purchases are made and the card remains active until the date you set.',
            )}
            hideRecurring={hideRecurringOption}
            hideGuestCards
          />
        ))}
      {screen === RequestVirtualCardScreen.Standard && (
        <RequestVirtualCardStandardContent
          receiptAttachments={receiptAttachments}
          uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
          postingVirtualCard={postingVirtualCard}
          uploadAttachments={dispatchUploadAttachments}
          deleteAttachment={deleteAttachment}
          postVirtualCardAndOpenToastAndCloseFlyout={
            dispatchPostVirtualCardAndOpenToastAndCloseFlyout
          }
          setScreen={setScreen}
          topLevelError={topLevelError}
          errorDetailMap={errorDetailMap}
          userOptions={filteredUserEmailOptions}
          recentUserOptions={filteredRecentUserEmailOptions}
          userInfiniteScrollProps={userInfiniteScrollProps}
        />
      )}
      {screen === RequestVirtualCardScreen.Recurring && (
        <RequestVirtualCardRecurringContent
          receiptAttachments={receiptAttachments}
          uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
          postingVirtualCard={postingVirtualCard}
          uploadAttachments={dispatchUploadAttachments}
          deleteAttachment={deleteAttachment}
          postVirtualCardAndOpenToastAndCloseFlyout={
            dispatchPostVirtualCardAndOpenToastAndCloseFlyout
          }
          setScreen={setScreen}
          topLevelError={topLevelError}
          errorDetailMap={errorDetailMap}
          userOptions={filteredUserEmailOptions}
          recentUserOptions={filteredRecentUserEmailOptions}
          userInfiniteScrollProps={userInfiniteScrollProps}
        />
      )}
      {screen === RequestVirtualCardScreen.DecliningBalance && (
        <RequestVirtualCardStandardContent
          receiptAttachments={receiptAttachments}
          uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
          postingVirtualCard={postingVirtualCard}
          uploadAttachments={dispatchUploadAttachments}
          deleteAttachment={deleteAttachment}
          postVirtualCardAndOpenToastAndCloseFlyout={
            dispatchPostVirtualCardAndOpenToastAndCloseFlyout
          }
          setScreen={setScreen}
          topLevelError={topLevelError}
          errorDetailMap={errorDetailMap}
          userOptions={filteredUserEmailOptions}
          recentUserOptions={filteredRecentUserEmailOptions}
          userInfiniteScrollProps={userInfiniteScrollProps}
          isBalanceLocked
        />
      )}
      {screen === RequestVirtualCardScreen.IssuerCycle && (
        <RequestVirtualCardIssuerCycleContent
          receiptAttachments={receiptAttachments}
          uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
          postingVirtualCard={postingVirtualCard}
          uploadAttachments={dispatchUploadAttachments}
          deleteAttachment={deleteAttachment}
          postVirtualCardAndOpenToastAndCloseFlyout={
            dispatchPostVirtualCardAndOpenToastAndCloseFlyout
          }
          setScreen={setScreen}
          topLevelError={topLevelError}
          errorDetailMap={errorDetailMap}
          userOptions={filteredUserEmailOptions}
          recentUserOptions={filteredRecentUserEmailOptions}
          userInfiniteScrollProps={userInfiniteScrollProps}
        />
      )}
    </RightFlyout>
  )
}

export default RequestVirtualCard
